// src/components/AppLayout.tsx
import React from 'react';
import { Layout, Menu, Avatar, Dropdown, Typography } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  DashboardOutlined,
  TeamOutlined,
  FileTextOutlined,
  AuditOutlined,
  ProjectOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import NotificationCenter from './NotificationCenter';
import type { MenuProps } from 'antd';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useAuthStore((state) => state.currentUser);
  const logout = useAuthStore((state) => state.logout);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Cerrar Sesión',
      onClick: handleLogout,
    },
  ];

  // Menú según el rol del usuario
  const getMenuItems = (): MenuProps['items'] => {
    if (currentUser?.role === 'admin') {
      return [
        {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: 'Dashboard',
          onClick: () => navigate('/dashboard'),
        },
        {
          key: '/dashboard/clients',
          icon: <TeamOutlined />,
          label: 'Clientes',
          onClick: () => navigate('/dashboard/clients'),
        },
        {
          key: '/dashboard/users',
          icon: <UserOutlined />,
          label: 'Usuarios',
          onClick: () => navigate('/dashboard/users'),
        },
        {
          key: '/dashboard/projects',
          icon: <ProjectOutlined />,
          label: 'Proyectos',
          onClick: () => navigate('/dashboard/projects'),
        },
        {
          key: '/dashboard/tasks',
          icon: <CheckSquareOutlined />,
          label: 'Tareas',
          onClick: () => navigate('/dashboard/tasks'),
        },
        {
          key: '/dashboard/documents',
          icon: <FileTextOutlined />,
          label: 'Documentos',
          onClick: () => navigate('/dashboard/documents'),
        },
        {
          key: '/dashboard/audits',
          icon: <AuditOutlined />,
          label: 'Auditorías',
          onClick: () => navigate('/dashboard/audits'),
        },
      ];
    } else {
      return [
        {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: 'Dashboard',
          onClick: () => navigate('/dashboard'),
        },
        {
          key: '/dashboard/tasks',
          icon: <CheckSquareOutlined />,
          label: 'Mis Tareas',
          onClick: () => navigate('/dashboard/tasks'),
        },
        {
          key: '/dashboard/documents',
          icon: <FileTextOutlined />,
          label: 'Mis Documentos',
          onClick: () => navigate('/dashboard/documents'),
        },
        {
          key: '/dashboard/audits',
          icon: <AuditOutlined />,
          label: 'Mis Auditorías',
          onClick: () => navigate('/dashboard/audits'),
        },
      ];
    }
  };

  const selectedKey = location.pathname;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        background: '#001529',
        padding: '0 24px'
      }}>
        <Title level={3} style={{ color: 'white', margin: 0 }}>
          HR Partner Pro
        </Title>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <NotificationCenter />
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} style={{ marginRight: 8 }} />
              <span style={{ color: 'white' }}>{currentUser?.name}</span>
            </div>
          </Dropdown>
        </div>
      </Header>
      <Layout>
        <Sider width={250} style={{ background: '#fff' }}>
          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            items={getMenuItems()}
            style={{ height: '100%', borderRight: 0 }}
          />
        </Sider>
        <Layout style={{ padding: '24px' }}>
          <Content
            style={{
              background: '#fff',
              padding: 24,
              margin: 0,
              minHeight: 280,
            }}
          >
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
